import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { apiGet } from "@/lib/api";
import { ArrowRight, CheckCircle2, ListChecks } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import usePageSEO from "@/hooks/usePageSEO";

export default function ScenarioDetail() {
  const { slug } = useParams();
  const [s, setS] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    apiGet(`/scenarios/${slug}`).then((data) => {
      if (!cancelled) setS(data);
    }).catch(() => {
      if (!cancelled) setError("Case study not found.");
    });
    return () => { cancelled = true; };
  }, [slug]);

  usePageSEO(s ? {
    title: s.headline,
    description: s.summary,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: s.headline,
      description: s.summary,
    },
  } : { title: "Case Study", description: "Layered strategy case study" });

  if (error) return <ErrorPage msg={error} />;
  if (!s) return <LoadingPage />;

  const applyHref = `/apply?source=case-study-${s.slug}`;

  return (
    <div data-testid={`scenario-page-${s.slug}`} className="min-h-screen bg-lw-ivory">
      <Nav />
      <main className="pt-28 sm:pt-32 lg:pt-36 pb-20">
        <article className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12">
          <span data-testid="hypothetical-badge" className="lw-pill bg-lw-navy text-lw-ivory border-lw-navy">Hypothetical example</span>
          <h1 className="mt-4 font-serif-display text-4xl sm:text-5xl text-lw-navy leading-[1.05]">{s.headline}</h1>
          <p className="mt-4 text-base sm:text-lg text-lw-muted leading-relaxed">{s.summary}</p>

          <section className="mt-12">
            <h2 className="font-serif-display text-2xl text-lw-navy">The situation</h2>
            <p className="mt-3 text-sm text-lw-navy/85 leading-relaxed font-semibold">{s.situation}</p>
            <div className="mt-4 prose prose-sm max-w-none text-lw-navy/85 leading-relaxed">
              {s.narrative_full.split("\n\n").map((p, i) => <p key={i} className="mb-3">{p}</p>)}
            </div>
          </section>

          <section className="mt-12">
            <h2 className="font-serif-display text-2xl text-lw-navy">Top concerns</h2>
            <ul data-testid="scenario-concerns" className="mt-4 space-y-2.5">
              {s.concerns.map((c, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-lw-navy/85">
                  <CheckCircle2 size={18} className="text-lw-gold mt-0.5 flex-shrink-0" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="mt-12">
            <h2 className="font-serif-display text-2xl text-lw-navy">The layered strategy</h2>
            <div data-testid="scenario-strategy" className="mt-4 overflow-x-auto rounded-2xl border border-lw-navy/10 bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-lw-ivory-warm text-left">
                    <th className="p-4 font-sans-ui text-xs uppercase tracking-wider text-lw-navy/60">Product</th>
                    <th className="p-4 font-sans-ui text-xs uppercase tracking-wider text-lw-navy/60">Role</th>
                    <th className="p-4 font-sans-ui text-xs uppercase tracking-wider text-lw-navy/60">Premium</th>
                    <th className="p-4 font-sans-ui text-xs uppercase tracking-wider text-lw-navy/60">Why</th>
                  </tr>
                </thead>
                <tbody>
                  {s.strategy.map((row, i) => (
                    <tr key={i} className="border-t border-lw-navy/5 align-top">
                      <td className="p-4 font-semibold text-lw-navy whitespace-nowrap">{row.product}</td>
                      <td className="p-4 text-lw-navy/80">{row.role}</td>
                      <td className="p-4 text-lw-navy/80 whitespace-nowrap">{row.premium}</td>
                      <td className="p-4 text-lw-muted">{row.why}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="mt-12">
            <h2 className="font-serif-display text-2xl text-lw-navy">Projected outcomes</h2>
            <div data-testid="scenario-outcomes" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-4">
              {s.outcomes.map((o, i) => (
                <div key={i} className="rounded-xl border border-lw-gold/40 bg-white p-4">
                  <p className="text-[11px] uppercase tracking-wider text-lw-navy/55">{o.label}</p>
                  <p className="mt-2 font-serif-display text-lg text-lw-navy tabular-nums leading-tight">{o.value}</p>
                </div>
              ))}
            </div>
            <p className="mt-3 text-[11px] italic text-lw-muted leading-relaxed">{s.hypothetical_note}</p>
          </section>

          <section className="mt-12">
            <h2 className="font-serif-display text-2xl text-lw-navy">What this teaches</h2>
            <ul data-testid="scenario-takeaways" className="mt-4 space-y-2.5">
              {s.takeaways.map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-lw-navy/85">
                  <ListChecks size={18} className="text-lw-gold mt-0.5 flex-shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="mt-12">
            <div className="rounded-3xl bg-lw-ivory-warm border border-lw-gold/30 p-8 sm:p-10">
              <h2 className="font-serif-display text-2xl sm:text-3xl text-lw-navy leading-tight max-w-xl">Could this work for you?</h2>
              <p className="mt-3 text-sm text-lw-muted max-w-xl">Book a 30-minute discovery call and we&apos;ll model the numbers against your real situation.</p>
              <Link to={applyHref} data-testid="scenario-cta" className="btn-gold mt-6 inline-flex">Start the conversation <ArrowRight size={18} /></Link>
            </div>
          </section>

          <div data-testid="scenario-hypothetical-block" className="mt-12 rounded-2xl border border-lw-gold/40 bg-lw-ivory-warm p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-lw-navy/70">Hypothetical illustration</p>
            <p className="mt-2 text-xs text-lw-muted leading-relaxed">
              {s.hypothetical_note} This is a hypothetical illustration for educational purposes only. It does not represent an actual client and is not a testimonial. Individual results depend on many factors including age, health, financial situation, product selection, carrier, and market performance. Actual policy performance is not guaranteed. Consult a licensed insurance professional and your tax/legal advisor before making decisions. Coverage, premium, and outcomes shown are subject to underwriting, carrier approval, and individual circumstances. Lifespan Wealth Partners does not provide legal or tax advice.
            </p>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}

function LoadingPage() {
  return (
    <div className="min-h-screen bg-lw-ivory">
      <Nav />
      <main className="max-w-3xl mx-auto px-5 pt-32 pb-20"><p className="text-lw-muted">Loading…</p></main>
      <Footer />
    </div>
  );
}
function ErrorPage({ msg }) {
  return (
    <div className="min-h-screen bg-lw-ivory">
      <Nav />
      <main className="max-w-3xl mx-auto px-5 pt-32 pb-20">
        <h1 className="font-serif-display text-3xl text-lw-navy">Not found.</h1>
        <p className="mt-3 text-lw-muted">{msg}</p>
        <Link to="/insights/scenarios" className="btn-gold mt-6 inline-flex">See all case studies</Link>
      </main>
      <Footer />
    </div>
  );
}
